import { Button, Fieldset, Form, Heading, Layout, Spinner } from '@vwfs-bronson/bronson-react';
import { Formik, FormikHelpers } from 'formik';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { formAutocomplete } from '../../config';
import {
  QuoteCalculationServicePlanResponseModel,
  useQuoteCalculateMutation,
} from '../../services/redux/features/api/quoteCalculate.api-slice';
import { journeyDataActions, journeyDataSelectors } from '../../services/redux/features/journeyData.slice';
import { VehicleType } from '../../types/vehicleTypeSelect';
import { UsedCarSelectionFormFields } from '../UsedCarSelectionForm/UsedCarSelectionFormModels';
import { AdditionalServicePlanRemoveModal } from './components/AdditionalServicePlanRemoveModal';
import { AdditionalServicePlanFormModel } from './form.model';
import { createFormInputs } from './formInputs';
import { additionalServicePlanInitialValues } from './initialValues';
import { additionalServicePlanValidationSchema } from './validationSchema';

const populateWithUsedCarData = (
  sourceData: AdditionalServicePlanFormModel,
  carData: UsedCarSelectionFormFields
): AdditionalServicePlanFormModel => {
  return {
    ...sourceData,
    firstRegistrationDate: carData?.firstRegistration,
  };
};

interface AdditionalServicePlanProps {
  vehicleType: VehicleType;
  usedCarData: UsedCarSelectionFormFields | null;
}

export const servicePlanData = [
  { label: 'Audi Summer Service Pack 2023', key: 'audi_summer_service_pack_2023', contractDuration: 12 },
  { label: 'ID.3 Large 232 Pack', key: 'id3_large_232_pack', contractDuration: 36 },
  {
    label: 'SKODA New Services Pack (2 Services)',
    key: 'skoda_new_services_pack_2_services',
    contractDuration: 48,
  },
];

export const AdditionalServicePlan: FunctionComponent<AdditionalServicePlanProps> = ({ vehicleType, usedCarData }) => {
  const { t } = useTranslation();
  const [calculate, { isLoading, data }] = useQuoteCalculateMutation();
  const dispatch = useDispatch();

  const [initialValues, setInitialValues] = useState(additionalServicePlanInitialValues);
  const [isCalculationPending, setIsCalculationPending] = useState(false);

  const [isRemoveOfServicePlanModalVisible, setIsRemoveOfServicePlanModalVisible] = useState(false);

  const isAdditionalServicePlanAdded = useSelector(journeyDataSelectors.isAdditionalServicePlanAdded);
  const additionalServicePlanValues = useSelector(journeyDataSelectors.additionalServicePlan);

  const [inputs /* , updateInputs */] = useState<ReturnType<typeof createFormInputs>>(() => createFormInputs());

  const servicePlanList = servicePlanData.map((servicePlan) => ({ label: servicePlan.label, value: servicePlan.key }));
  servicePlanList.unshift({ label: t('formControls:select.noSelection'), value: '' });

  useEffect(
    function populateForm() {
      if (usedCarData) {
        const populatedInitialValue: AdditionalServicePlanFormModel = populateWithUsedCarData(
          initialValues,
          usedCarData
        );

        setInitialValues(populatedInitialValue);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [usedCarData]
  );

  useEffect(() => {
    const performCalculation = async () => {
      const values = additionalServicePlanValues || {};

      const data = (await calculate({
        productType: 'servicePlan',
        paymentType: values.paymentType,
      }).unwrap()) as QuoteCalculationServicePlanResponseModel;

      const updatedFormValue: AdditionalServicePlanFormModel = {
        ...values,
        payment: data.payment,
        isQuoteValid: data.isQuoteValid,
        firstRegistrationDate: '',
        recordedKilometers: null,
        servicePlanName: '',
        contractDuration: '',
        paymentTerm: '',
      };

      dispatch(journeyDataActions.updateData({ additionalServicePlan: updatedFormValue }));
      setInitialValues(updatedFormValue);
    };

    if (isCalculationPending) {
      performCalculation();
    }

    setIsCalculationPending(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCalculationPending, setInitialValues]);

  return (
    <>
      <Spinner busy={isLoading} section>
        <AdditionalServicePlanRemoveModal
          isShown={isRemoveOfServicePlanModalVisible}
          setIsShow={setIsRemoveOfServicePlanModalVisible}
        />
        {initialValues && (
          <Formik
            validationSchema={additionalServicePlanValidationSchema(vehicleType)}
            initialValues={initialValues}
            validateOnMount
            validateOnChange={false}
            validateOnBlur
            onSubmit={async (
              values: AdditionalServicePlanFormModel,
              helpers: FormikHelpers<AdditionalServicePlanFormModel>
            ) => {
              const data = (await calculate({
                productType: 'servicePlan',
                paymentType: values.paymentType,
              }).unwrap()) as QuoteCalculationServicePlanResponseModel;

              helpers.setFieldValue('payment', data.payment);
              helpers.setFieldValue('isQuoteValid', data.isQuoteValid);

              helpers.setSubmitting(false);
            }}
          >
            {({ values, handleSubmit, setValues, setFieldValue, isValid }) => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              useEffect(() => {
                const contractDuration = servicePlanData.find(
                  (servicePlan) => servicePlan.key === values.servicePlanName
                )?.contractDuration;
                setFieldValue('contractDuration', contractDuration);
                // eslint-disable-next-line react-hooks/exhaustive-deps
              }, [values.servicePlanName]);

              // eslint-disable-next-line react-hooks/rules-of-hooks
              useEffect(() => {
                if (usedCarData) {
                  const populatedInitialValue: AdditionalServicePlanFormModel = populateWithUsedCarData(
                    initialValues,
                    usedCarData
                  );

                  setValues(populatedInitialValue);
                }
              }, [setValues]);

              // eslint-disable-next-line react-hooks/rules-of-hooks
              useEffect(() => {
                if (values.paymentType === 'upfront') {
                  setFieldValue('paymentTerm', undefined);
                }
              }, [values.paymentType, setFieldValue]);

              const paymentTermList = [
                { label: t('formControls:select.noSelection'), value: '' },
                { label: '12', value: '12' },
                { label: '24', value: '24' },
                { label: '36', value: '36' },
              ].filter((term) => {
                if (Number(values.paymentTerm) > Number(values.contractDuration)) {
                  setFieldValue('paymentTerm', undefined);
                }

                return Number(term.value) <= Number(values.contractDuration);
              });

              return (
                <>
                  {!isAdditionalServicePlanAdded && (
                    <Button
                      icon="semantic-add"
                      link
                      secondary
                      className="u-m"
                      onClick={() => dispatch(journeyDataActions.setIsAdditionalServicePlanAdded(true))}
                    >
                      {t('additionalServicePlan:addServicePlanButton')}
                    </Button>
                  )}
                  {isAdditionalServicePlanAdded && (
                    <div className="u-m">
                      <Layout>
                        <Layout.Item default="1/2">
                          <Heading level={6}>{t('additionalServicePlan:title')}</Heading>
                        </Layout.Item>
                        <Layout.Item default="1/2" className="u-text-right">
                          <Button
                            ariaLabel="Close section"
                            icon="semantic-close"
                            className="u-mr-small"
                            round
                            secondary
                            small
                            onClick={() => {
                              setIsRemoveOfServicePlanModalVisible(true);
                            }}
                          />
                        </Layout.Item>
                      </Layout>
                      <Form autoComplete={formAutocomplete} onSubmit={handleSubmit}>
                        <Fieldset.Row>
                          <Layout>
                            <Layout.Item default="1/3" s="1/1">
                              {inputs.firstRegistrationDate()}
                            </Layout.Item>
                            <Layout.Item default="1/3" s="1/1">
                              {inputs.recordedKilometers()}
                            </Layout.Item>
                          </Layout>
                        </Fieldset.Row>
                        <Fieldset.Row>
                          <Layout>
                            <Layout.Item default="6/12">{inputs.servicePlanName(servicePlanList)}</Layout.Item>
                            <Layout.Item default="3/12">
                              <Button secondary full className="u-mt" type="button">
                                {t('additionalServicePlan:additionalServicePlanForm.searchServicePlanButton')}
                              </Button>
                            </Layout.Item>
                          </Layout>
                        </Fieldset.Row>
                        <Fieldset.Row>
                          <Layout>
                            <Layout.Item default="1/3" s="1/1">
                              {inputs.paymentType()}
                            </Layout.Item>
                            <Layout.Item default="1/3" s="1/1">
                              {inputs.contractDuration()}
                            </Layout.Item>
                            {values.paymentType !== 'upfront' && (
                              <Layout.Item default="1/3" s="1/1">
                                {inputs.paymentTerm(paymentTermList)}
                              </Layout.Item>
                            )}
                          </Layout>
                        </Fieldset.Row>
                        <Fieldset.Row>
                          <Layout>
                            <Layout.Item default="2/3" s="1/1">
                              <Layout>
                                <Layout.Item default="2/5">
                                  <Heading className="u-text-left u-m-none u-mt-small" level={6}>
                                    {values.paymentType === 'monthly' &&
                                      t(`additionalServicePlan:additionalServicePlanForm.monthlyPayment.label`)}
                                    {values.paymentType === 'upfront' &&
                                      t(`additionalServicePlan:additionalServicePlanForm.upfrontPayment.label`)}
                                  </Heading>
                                </Layout.Item>
                                <Layout.Item default="3/5">
                                  <Layout>
                                    <Layout.Item default="4/6" s="1/1">
                                      {inputs.payment(data)}
                                    </Layout.Item>
                                  </Layout>
                                </Layout.Item>
                              </Layout>
                            </Layout.Item>
                            <Layout.Item default="1/3" s="1/1">
                              <Button
                                secondary
                                full
                                disabled={!isValid}
                                type="button"
                                onClick={() => {
                                  setIsCalculationPending(true);
                                }}
                              >
                                {t('additionalServicePlan:additionalServicePlanForm.calculateButton')}
                              </Button>
                            </Layout.Item>
                          </Layout>
                        </Fieldset.Row>
                      </Form>
                    </div>
                  )}
                </>
              );
            }}
          </Formik>
        )}
      </Spinner>
    </>
  );
};
